// Proposals & quotes send register (workflow D1) — the formatting and
// summary maths the Stats band, the Log sent form and the detail-page rails
// share, plus the rails' small fetch hook. The pure helpers stay React-free
// at the top so the Node-only suite can test them.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { formatMoney } from './moneyFormat';
import {
  DOCUMENT_SEND_KIND_LABELS,
  DOCUMENT_SEND_OUTCOME_LABELS,
  type DocumentSendListItem,
  type DocumentSendOutcome,
} from '../../shared/types';
import type { PillTone } from '../components/primitives';

export const OUTCOME_TONE: Record<DocumentSendOutcome, PillTone> = {
  pending: undefined,
  won: 'ok',
  lost: 'danger',
  on_hold: 'gold',
};

export const outcomeLabel = (o: DocumentSendOutcome) => DOCUMENT_SEND_OUTCOME_LABELS[o];
export const kindLabel = (s: Pick<DocumentSendListItem, 'kind'>) => DOCUMENT_SEND_KIND_LABELS[s.kind];

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-10" / "2026-10-03" → "October 2026". */
export function monthTitle(iso: string): string {
  return `${MONTHS_FULL[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
}

/** "$12,460" — whole dollars; the register reads at a glance, not to the cent. */
export function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-AU')}`;
}

/** "$12,460–16,400" when quoted as a range, "$12,460" otherwise, "—" when
    no amount was recorded. */
export function amountOf(s: Pick<DocumentSendListItem, 'amount_min_cents' | 'amount_max_cents'>): string {
  const min = s.amount_min_cents;
  const max = s.amount_max_cents;
  if (min === null) return max === null ? '—' : dollars(max);
  if (max !== null && max !== min) return `${dollars(min)}–${dollars(max).slice(1)}`;
  return dollars(min);
}

/** Phone rows: the low end plus "+" when quoted as a range; the high end
    alone when only that is known; "—" when no amount. */
export function compactAmountOf(s: Pick<DocumentSendListItem, 'amount_min_cents' | 'amount_max_cents'>): string {
  const { amount_min_cents: min, amount_max_cents: max } = s;
  if (min === null) return max === null ? '—' : formatMoney(max);
  return `${formatMoney(min)}${max !== null && max !== min ? '+' : ''}`;
}

export interface SendSummary {
  sent: number;
  proposals: number;
  quotes: number;
  won: number;
  /** Rounded % of won among decided (won + lost); 0 when nothing decided. */
  wonPct: number;
  pending: number;
  onHold: number;
  valueSentCents: number;
  valueWonCents: number;
}

/** The band's vitals, computed from the sends in the Stats window. */
export function summarizeSends(sends: readonly DocumentSendListItem[]): SendSummary {
  let proposals = 0, won = 0, lost = 0, pending = 0, onHold = 0, valueSent = 0, valueWon = 0;
  for (const s of sends) {
    if (s.kind === 'proposal') proposals++;
    valueSent += s.amount_min_cents ?? 0;
    if (s.outcome === 'won') {
      won++;
      valueWon += s.won_amount_cents ?? s.amount_min_cents ?? 0;
    } else if (s.outcome === 'lost') lost++;
    else if (s.outcome === 'pending') pending++;
    else if (s.outcome === 'on_hold') onHold++;
  }
  const decided = won + lost;
  return {
    sent: sends.length,
    proposals,
    quotes: sends.length - proposals,
    won,
    wonPct: decided ? Math.round((won / decided) * 100) : 0,
    pending,
    onHold,
    valueSentCents: valueSent,
    valueWonCents: valueWon,
  };
}

/** Newest month first; rows keep the server's sent_date DESC order. */
export function groupByMonth<T extends { sent_date: string }>(sends: readonly T[]): { month: string; sends: T[] }[] {
  const groups: { month: string; sends: T[] }[] = [];
  const sorted = [...sends].sort((a, b) => b.sent_date.localeCompare(a.sent_date));
  for (const s of sorted) {
    const month = s.sent_date.slice(0, 7);
    const last = groups[groups.length - 1];
    if (last && last.month === month) last.sends.push(s);
    else groups.push({ month, sends: [s] });
  }
  return groups;
}

/** Search haystack: title, client, ref, linked project/lead names. */
export function matchesSendQuery(s: DocumentSendListItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [s.title, s.client_label, s.ref, s.project_name, s.lead_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

/** "Open ↗" target for proposal rows: the tool admin URL opens in the OS
    tool embed (same route as ProjectDetail's artifact rows), else the public
    URL in a new tab; quotes have nothing to open. */
export function sendOpenTarget(s: DocumentSendListItem): { internal: string } | { external: string } | null {
  if (s.kind !== 'proposal') return null;
  const adminUrl = s.artifact_admin_url ?? s.admin_url;
  if (adminUrl) return { internal: `/tools/proposal?url=${encodeURIComponent(adminUrl)}` };
  if (s.artifact_public_url) return { external: s.artifact_public_url };
  return null;
}

// ── Fetching ─────────────────────────────────────────────────────────────

/** Sends for one lead or project (detail rails), re-fetched on `ss:mutated`
    (coalesced 250ms, the pattern every page uses) and when the id changes. */
export function useDocumentSends(filter: { lead_id: string } | { project_id: string }) {
  const [sends, setSends] = useState<DocumentSendListItem[] | null>(null);
  const key = 'lead_id' in filter ? `lead_id=${encodeURIComponent(filter.lead_id)}` : `project_id=${encodeURIComponent(filter.project_id)}`;
  const seq = useRef(0);
  const load = useCallback(() => {
    const mine = ++seq.current;
    api<{ sends: DocumentSendListItem[] }>(`/api/admin/document-sends?${key}`)
      .then((r) => {
        if (mine === seq.current) setSends(r.sends);
      })
      .catch(() => {
        if (mine === seq.current) setSends([]);
      });
  }, [key]);
  useEffect(() => {
    setSends(null);
    load();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onMutated = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(load, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (timer) clearTimeout(timer);
      seq.current++;
    };
  }, [load]);
  return sends;
}
